import { useEffect, MouseEvent, useRef, useState } from 'react';
import {
	AutoFocus,
	BadgeText,
	BreakPoints,
	Button,
	CellulaFormSubmitData,
	DialogAlert,
	DialogModal,
	Flex,
	Form,
	IconButton,
	InputCombobox,
	Sheet,
	Text,
	useBreakpoint,
	useFormContext,
} from '@cellula/react';
import { tokens } from '@cellula/react-theme-patient';
import { TopBar } from '@component/top_bar.component';
import { useResolve, useResolveController, useService, withNewContainer } from '@visiba/instantiation';
import { Title } from '@component/title.component';
import { Route, Routes, useParams } from 'react-router-dom';
import { ScreenTimeSlotController } from './screen_time_slot.controller';
import { Async } from '@component/async.component';
import { DataList } from '@component/data_list.component';
import { withTranslation } from '@application/i18n/with_translation';
import { TimeSlotInfo } from '../screen_conclusion/screen_conclusion.component';
import { LocalityType } from '@api_patient/generated/clientv2/models/locality-type';
import { AuthService } from '@application/authentication/auth.service';
import { Auth } from '@feature/auth/auth.component';
import { WeeklyCalender } from '@component/weekly_calender.component';
import { capitalizeFirstLetter } from '@module/capitalize_first_letter';
import styled from 'styled-components';
import { ActionCard } from '@component/action_card.component';
import { DateDisplayFormats, DateService } from '@application/time/date.service';
import { AvatarInfo } from '@component/avatar_info.component';
import { Shimmer } from '@component/new_shimmer.component';
import { I18nAndL10n } from '@application/i18n/mod';
import { ViewBookingAnalyticActions, ViewBookingController } from '../view_booking.controller';

import { AnimatePresence, motion } from 'framer-motion';
import { useAnalyticContext } from '@application/analytic/analytic_link/mod';

export const ScreenTimeSlot = withNewContainer(function ScreenTimeSlot() {
	return (
		<>
			<TopBar.Content goBack />

			<Routes>
				<Route path='digital' element={<ScreenTimeSlotContent localityType={LocalityType.Digital} />} />
				<Route path=':locationId' element={<ScreenTimeSlotContent localityType={LocalityType.Physical} />} />
			</Routes>
		</>
	);
});

function ScreenTimeSlotContent({ localityType }: ScreenTimeSlotContentProps) {
	const { locationId } = useParams();
	const controller = useResolveController(ScreenTimeSlotController);
	const triggerButtonRef = useRef<HTMLButtonElement | null>(null);
	const isSmallDevice = useBreakpoint(BreakPoints.Small);
	const analytic = useAnalyticContext();
	const dateService = useService(DateService);
	const containerRef = useRef<HTMLDivElement>(null);
	const [timeSlotDetailsOpen, setTimeSlotDetailsOpen] = useState(false);
	const dateSpan = controller.dateSpan.value;

	useEffect(
		function whenTimeSlotDetailsDataChanges() {
			if (controller.timeSlotDetails.peek().flowState === 'idle') {
				setTimeSlotDetailsOpen(false);

				return;
			}

			setTimeSlotDetailsOpen(true);
		},
		[controller.timeSlotDetails, controller.timeSlotDetails.value],
	);

	function handleTimeSlotDetailsOpenChange(open: boolean) {
		setTimeSlotDetailsOpen(open);

		if (!open) {
			analytic.trackEvent(ViewBookingAnalyticActions.CloseTimeSlotDetails);
			controller.clearTimeSlotDetails();
		}
	}

	function handlePreviewTimeSlot(timeSlotId: string) {
		return (event: MouseEvent<HTMLButtonElement>) => {
			analytic.trackEvent('Click time slot');
			triggerButtonRef.current = event.currentTarget as HTMLButtonElement;
			controller.previewTimeSlot(timeSlotId);
		};
	}

	function handleGoToNextAvailableDate() {
		const nextAvailableDate = controller.nextAvailableDate.peek();
		if (nextAvailableDate) {
			controller.setCurrentDate(nextAvailableDate);
		}
	}

	function handleAutoFocusOnDetailClose(event: Event) {
		event.preventDefault();

		triggerButtonRef.current?.focus();
	}

	function handleFilterChange(formData: CellulaFormSubmitData) {
		controller.setFilter({
			resourceId: formData.resourceId ?? null,
			specialityId: formData.specialityId ?? null,
			unitId: formData.unitId ?? null,
		});
	}

	useEffect(
		function whenMount() {
			async function fetchData() {
				// We don't care about errors here
				await controller.clearTimeSlotDetails();

				controller.setup(localityType, locationId);
			}
			fetchData();

			if (__visiba_feature_flags__['CareExperience.PW.Idp']) {
				// Nice, we don't need to do any quirks when using IDP 😌
			} else {
				function handleWhenOldSignInIsTriggered() {
					setTimeSlotDetailsOpen(false);
				}

				function handleWhenOldSignInIsAborted() {
					controller.clearSelectTimeSlot();
					setTimeSlotDetailsOpen(true);
				}

				window.addEventListener('triggerSignIn', handleWhenOldSignInIsTriggered);
				window.addEventListener('abortSignIn', handleWhenOldSignInIsAborted);

				return () => {
					window.removeEventListener('triggerSignIn', handleWhenOldSignInIsTriggered);
					window.removeEventListener('abortSignIn', handleWhenOldSignInIsAborted);
				};
			}
		},
		[controller, localityType, locationId],
	);

	useEffect(
		function setFocusToTimeSlotListWhenChangingDate() {
			containerRef.current?.focus();
		},
		[controller.nextAvailableDate.value],
	);

	return (
		<Flex gap={tokens.spacing.x2_5}>
			<Flex gap={tokens.spacing.x3}>
				<Flex gap={tokens.spacing.x3} style={{ position: 'relative' }}>
					<Flex gap={tokens.spacing.x1}>
						<Title title={withTranslation('View.Booking.ScreenTimeSlot.Title')} titleSize='small' />
						<Text variant='labelSemiBold'>
							{localityType === LocalityType.Digital
								? withTranslation('View.Booking.ScreenTimeSlot.DigitalVideoAppointment')
								: controller.location.value?.Name}
						</Text>
					</Flex>

					<Form.Root onSubmit={() => {}} options={{}} onInputChange={handleFilterChange}>
						<TimeSlotFilters />
					</Form.Root>
				</Flex>

				<WeeklyCalender
					date={controller.currentDate.value ?? undefined}
					fromDate={dateSpan.from ?? undefined}
					toDate={dateSpan.to ?? undefined}
					notifications={controller.availability.value.dates}
					onDayClick={(date) => {
						controller.setCurrentDate(date);
					}}
					onDateChange={(date) => {
						controller.setCurrentDate(date);
					}}
				/>
			</Flex>
			<Flex gap={tokens.spacing.x1} ref={containerRef} tabIndex={-1} style={{ outline: 'none' }}>
				{controller.currentDate.value ? (
					<Text variant='labelSmallRegular'>
						{capitalizeFirstLetter(
							controller.currentDate.value?.format(
								dateService.getLocaleSpecificDateFormatTemplate(DateDisplayFormats.FULL_DISPLAY_NO_TIME),
							),
						)}
					</Text>
				) : null}

				<Async
					derive={controller.timeSlots}
					fallback={
						<Flex gap={tokens.spacing.x1}>
							<Shimmer height={80} width={'100%'} />
							<Shimmer height={80} width={'100%'} opacity={0.7} />
							<Shimmer height={80} width={'100%'} opacity={0.4} />
						</Flex>
					}
				>
					{(timeSlots) => (
						<Flex gap={tokens.spacing.x2} style={{ outline: 'none' }}>
							{timeSlots.length > 0 ? (
								<Flex gap={tokens.spacing.x1}>
									<ol
										aria-label={withTranslation('View.Booking.ScreenTimeSlot.TimeSlotList.AriaLabel', {
											date: controller.currentDate.value?.format('LL') ?? '',
										})}
										style={{ display: 'contents' }}
									>
										{timeSlots.map((timeSlot) => {
											const startDate = dateService.newDate(timeSlot.Start);
											const ariaLabel = `${I18nAndL10n.translate('View.Booking.ScreenTimeSlot.TimeSlot.AriaLabel', {
												appointmentType: '',
												startTime: startDate.format('HH:mm'),
												endTime: startDate.add(timeSlot.Duration, 'minutes').format('HH:mm'),
												hcpName: timeSlot.Resource?.Name ?? '',
												hcpTitle: timeSlot.Resource?.Title ?? '',
											})} ${timeSlot.Price ? `${I18nAndL10n.translate('Common.Label.Cost')}: ${timeSlot.Price.Formatted}` : ''}`;

											return (
												<li key={timeSlot.Id} style={{ display: 'contents' }}>
													<ActionCard.Root onClick={handlePreviewTimeSlot(timeSlot.Id ?? '')} aria-label={ariaLabel} hideArrow>
														<Flex direction='row' gap={tokens.spacing.x2} justify='space-between' grow='1'>
															<Flex gap={tokens.spacing.x0_25}>
																<ActionCard.Title>{dateService.newDate(timeSlot.Start).format('HH:mm')}</ActionCard.Title>
																<ActionCard.Meta>
																	{timeSlot.Duration} {withTranslation('Common.Word.Minutes').toLocaleLowerCase()}
																</ActionCard.Meta>
															</Flex>
															<Flex align='flex-end' gap={tokens.spacing.x1}>
																{timeSlot.Resource ? (
																	<AvatarInfo
																		name={timeSlot.Resource.Name ?? undefined}
																		title={timeSlot.Resource.Title ?? undefined}
																		avatarSrc={timeSlot.Resource.ProfileImageUrl ?? undefined}
																		align='right'
																	/>
																) : null}

																{timeSlot.Price?.Value ? <BadgeText variant='info'>{timeSlot.Price?.Formatted}</BadgeText> : null}
															</Flex>
														</Flex>
													</ActionCard.Root>
												</li>
											);
										})}
									</ol>
								</Flex>
							) : (
								<EmptyState>{withTranslation('View.Booking.ScreenTimeSlot.NoAvailableTimes')}</EmptyState>
							)}

							{controller.nextAvailableDate.value ? (
								<Button variant='ghost' onClick={handleGoToNextAvailableDate} icon='arrowRight' iconPosition='trailing'>
									{withTranslation('View.Booking.ScreenTimeSlot.ButtonNextAvailableTime')}
								</Button>
							) : null}
						</Flex>
					)}
				</Async>
			</Flex>
			{isSmallDevice ? (
				<Sheet
					label={withTranslation('View.Booking.TimeSlotSheet.Label')}
					open={timeSlotDetailsOpen}
					onOpenChange={handleTimeSlotDetailsOpenChange}
					onCloseAutoFocus={handleAutoFocusOnDetailClose}
				>
					<Flex
						grow='1'
						style={{ width: '100%', maxWidth: '660px', margin: '0 auto', padding: `${tokens.spacing.x2} ${tokens.spacing.x2} 0` }}
					>
						<TimeSlotDetails />
					</Flex>
				</Sheet>
			) : (
				<DialogModal.Root
					open={timeSlotDetailsOpen}
					onOpenChange={handleTimeSlotDetailsOpenChange}
					onOpenAutoFocus={(e) => e.preventDefault()}
					onCloseAutoFocus={handleAutoFocusOnDetailClose}
				>
					<DialogModal.Content>
						<Flex grow='1' gap={tokens.spacing.x2}>
							<DialogModal.Title>{withTranslation('View.Booking.TimeSlotSheet.Label')}</DialogModal.Title>
							<TimeSlotDetails />
						</Flex>
					</DialogModal.Content>
				</DialogModal.Root>
			)}
			{controller.showUnavailableTimeSlotDialog.value ? <TimeSlotNotAvailableDialog /> : null}
		</Flex>
	);
}

function TimeSlotNotAvailableDialog() {
	const controller = useResolveController(ScreenTimeSlotController);
	const analyticCtx = useAnalyticContext();

	useEffect(
		function whenMount() {
			analyticCtx.trackEvent(ViewBookingAnalyticActions.OpenedTimeSlotNotAvailableDialog);
		},
		[analyticCtx],
	);

	function handleAbort() {
		analyticCtx.trackEvent(ViewBookingAnalyticActions.ClosedTimeSlotNotAvailableDialog);
		controller.showUnavailableTimeSlotDialog.value = false;
	}

	return (
		<DialogAlert
			isOpen
			header={withTranslation('View.Booking.TimeSlotUnavailableDialog.Header')}
			description={withTranslation('View.Booking.TimeSlotUnavailableDialog.Description')}
			abortText={withTranslation('Common.Button.Close')}
			onAbort={handleAbort}
		/>
	);
}

function TimeSlotFilters() {
	const viewBookingController = useResolveController(ViewBookingController);
	const controller = useResolveController(ScreenTimeSlotController);
	const formContext = useFormContext();
	const [visible, setVisible] = useState(false);
	const flowSettings = viewBookingController.getFlowSettings();
	const analytic = useAnalyticContext();

	function handleClearFilter() {
		analytic.trackEvent('Click clear and close filters');
		formContext?.control.reset({
			resourceId: null,
			specialityId: null,
			unitId: null,
		});
		setVisible(false);
	}

	function handleToggleFilter() {
		analytic.trackEvent('Click show filters');
		setVisible(true);
	}

	function handleFilterChange(filter: string) {
		return () => {
			analytic.trackEvent(`Changed filter: ${filter}`, formContext?.control.getValues(filter));
		};
	}

	useEffect(() => {
		const selectedFilters = controller.selectedFilters.peek();

		formContext?.control.reset({
			resourceId: selectedFilters.resourceId?.toString() ?? null,
			specialityId: selectedFilters.specialityId?.toString() ?? null,
			unitId: selectedFilters.unitId?.toString() ?? null,
		});

		setVisible(selectedFilters.resourceId != null || selectedFilters.specialityId != null || selectedFilters.unitId != null);
	}, [controller.selectedFilters, formContext?.control]);

	return (
		<>
			<IconButton
				icon='sliders'
				size='medium'
				text={withTranslation('View.Booking.ScreenTimeSlot.FilterToggleTooltip')}
				variant='interactive'
				onClick={handleToggleFilter}
				style={{ position: 'absolute', top: 0, right: 0, display: visible ? 'none' : 'block' }}
			/>
			<Async derive={controller.filters}>
				{(filters) => (
					<AnimatePresence>
						{visible ? (
							<motion.div
								style={{ overflow: 'hidden' }}
								initial={{ height: 0 }}
								animate={{ height: 'auto' }}
								transition={{ duration: 0.2, ease: 'easeInOut' }}
								exit={{ height: 0 }}
							>
								<AutoFocus>
									<Flex gap={tokens.spacing.x1} style={{ outline: 'none' }}>
										{filters?.resources && filters.resources.length > 0 ? (
											<InputCombobox.Root
												label={withTranslation('View.Booking.ScreenTimeSlot.FilterLabelStaff')}
												name='resourceId'
												optional={false}
												placeholder={withTranslation('Common.Word.All')}
												onChange={handleFilterChange('resourceId')}
												readOnly={flowSettings.lockFilters}
											>
												{filters.resources.map((resource) => (
													<InputCombobox.Item key={resource.Id} value={resource.Id.toString()} label={resource.Name ?? ''} />
												))}
											</InputCombobox.Root>
										) : null}
										{filters?.specialities && filters.specialities.length > 0 ? (
											<InputCombobox.Root
												label={withTranslation('View.Booking.ScreenTimeSlot.FilterLabelSpeciality')}
												name='specialityId'
												optional={false}
												placeholder={withTranslation('Common.Word.All')}
												onChange={handleFilterChange('specialityId')}
												readOnly={flowSettings.lockFilters}
											>
												{filters.specialities.map((speciality) => (
													<InputCombobox.Item key={speciality.Id} value={speciality.Id.toString()} label={speciality.Name ?? ''} />
												))}
											</InputCombobox.Root>
										) : null}
										{filters?.units && filters.units.length > 0 ? (
											<InputCombobox.Root
												label={withTranslation('View.Booking.ScreenTimeSlot.FilterLabelUnit')}
												name='unitId'
												optional={false}
												placeholder={withTranslation('Common.Word.All')}
												onChange={handleFilterChange('unitId')}
												readOnly={flowSettings.lockFilters}
											>
												{filters.units.map((unit) => (
													<InputCombobox.Item key={unit.Id} value={unit.Id.toString()} label={unit.Name ?? ''} />
												))}
											</InputCombobox.Root>
										) : null}
										{!flowSettings.lockFilters ? (
											<Flex align='end'>
												<Button variant='ghost' onClick={handleClearFilter} icon='cross' size='xSmall'>
													{withTranslation('View.Booking.ScreenTimeSlot.FilterClearButton')}
												</Button>
											</Flex>
										) : null}
									</Flex>
								</AutoFocus>
							</motion.div>
						) : null}
					</AnimatePresence>
				)}
			</Async>
		</>
	);
}

interface ScreenTimeSlotContentProps {
	localityType: LocalityType;
}

function TimeSlotDetails(): JSX.Element {
	const controller = useResolveController(ScreenTimeSlotController);
	const authService = useResolve(AuthService);
	const analyticCtx = useAnalyticContext();
	const isSignedIn = authService.user.value != null;

	function handleSelectTimeSlot(timeSlotId: string) {
		return () => {
			if (isSignedIn) {
				analyticCtx.trackEvent(ViewBookingAnalyticActions.SelectTimeSlot);
			} else {
				analyticCtx.trackEvent(ViewBookingAnalyticActions.SelectTimeSlotAndLogin);
			}
			controller.selectTimeSlot(timeSlotId, isSignedIn);
		};
	}

	function handleCloseSignInSheet() {
		controller.clearSelectTimeSlot();
	}

	useEffect(
		function whenMount() {
			analyticCtx.trackEvent(ViewBookingAnalyticActions.OpenTimeSlotDetails);
		},
		[analyticCtx],
	);

	return (
		<Async derive={controller.timeSlotDetails}>
			{(timeSlot) => (
				<>
					<Flex grow='1' gap={tokens.spacing.x2_5}>
						{timeSlot.price && timeSlot.price.Value ? (
							<DataList.Root>
								<DataList.Item label={withTranslation('Common.Label.Cost')}>
									<BadgeText variant='info'>{timeSlot.price.Formatted}</BadgeText>
								</DataList.Item>
							</DataList.Root>
						) : null}

						<TimeSlotInfo
							timeSlot={{
								duration: timeSlot.duration,
								physicalLocation: timeSlot.physicalLocation,
								resource: timeSlot.resource,
								start: timeSlot.start,
							}}
						/>
						{timeSlot.description ? (
							<Flex gap={tokens.spacing.x1}>
								<Text variant='labelSemiBold'>{withTranslation('View.Booking.TimeSlotInfo.Label.Description')}</Text>
								<Text variant='bodyRegular'>{timeSlot.description}</Text>
							</Flex>
						) : null}
						<Flex
							gap={tokens.spacing.x2}
							breakpointMediumAndUp={{
								direction: 'row-reverse',
								justify: 'space-between',
							}}
						>
							{isSignedIn ? (
								<Button variant='primary' onClick={handleSelectTimeSlot(timeSlot.id)}>
									{withTranslation('View.Booking.TimeSlotSheet.ButtonSelectTimeSlot')}
								</Button>
							) : (
								<Auth.SignInSheet
									returnUrl='/booking/forms'
									trigger={
										<Button variant='primary'>{withTranslation('View.Booking.TimeSlotSheet.ButtonLoginAndSelectTimeSlot')}</Button>
									}
									onOpen={handleSelectTimeSlot(timeSlot.id)}
									onClose={handleCloseSignInSheet}
								/>
							)}

							<Sheet.Close asChild>
								<Button variant='ghost' type='button'>
									{withTranslation('Common.Button.Abort')}
								</Button>
							</Sheet.Close>
						</Flex>
					</Flex>
				</>
			)}
		</Async>
	);
}

const EmptyState = styled.div`
	font: ${tokens.text.label.largeRegular};
	color: ${tokens.color.content.muted};
	border: 1px dashed ${tokens.color.border.default};
	border-radius: ${tokens.borderRadius.medium};
	padding: ${tokens.spacing.x4};
	text-align: center;
`;
